import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState as useNativeState } from "react";
import { useState, State } from "@hookstate/core";
import { Stage, Image, Layer } from "react-konva";
import { Layer as KonvaLayer } from "konva/lib/Layer";
import { ElementsType } from "./DrawingSheet";

import DrawingSheet from "./DrawingSheet";
import { Skeleton, useToast } from "@chakra-ui/react";
import { ImageElementsHolderType } from "../../pages/displayCertificate/DisplayCertificate";
import { OrganizationConfig } from "../../types/OrganizationConfig";

interface Bounds {
	height: number;
	width: number;
	top?: number;
	left?: number;
}

export interface Config {
	name: string;
	image_url: string;
	actual_dimensions: { width: number; height: number };
	rendered_dimensions: { width: number; height: number };
}

interface Props {
	elementsData: State<ElementsType | null>;
	configData: State<Config | null>;
	bg: string;
	imageLeftToRender?: number;
	isWhiteLabeled?: boolean;
	imageElementsHolder?: ImageElementsHolderType
	orgConfig?: OrganizationConfig | null
}

export interface PreviewCertificateType {
    getBase64Image: () => any,
}

const PreviewCertificate = forwardRef(({ elementsData, configData, bg, imageLeftToRender=0, isWhiteLabeled=false, imageElementsHolder, orgConfig }:Props, ref) => {
	const imageURL = useState<string>(bg);
	const isLoading = useState<boolean>(true);
	const [image, setImage] = useNativeState<HTMLImageElement | null>(null);

	const canvasBounds = useState<DOMRect>({ height: 1, width: 1 } as DOMRect);
	const konvaBounds = useState<Bounds>({ height: 1, width: 1 });
	const bgImgBounds = useState<Bounds>({ height: 1, width: 1 });
	const prevBounds = useState<Bounds>({ height: 0, width: 0 });
	const scale = useState<number>(1);
	const loader = useState<boolean>(false);
	const toast = useToast()

	const layerRef = useRef<KonvaLayer>(null);
	const imageRef = useRef<HTMLImageElement | null>(null);

	useImperativeHandle( ref, () => ({
        getBase64Image: () => {
			if (layerRef.current) {
				// Export at the template's actual size regardless of how many times the stage has been resized
				const actualHeight = configData.value?.actual_dimensions?.height;
				const stageHeight = konvaBounds.height.get();
				const pixelRatio = actualHeight && stageHeight > 0 ? actualHeight / stageHeight : 1;
				return layerRef.current.toDataURL({ pixelRatio, mimeType: "image/jpeg" });
			} else {
				toast({
					title: "Download Error",
					description: "Please wait while the canvas is loading",
					status: "error",
					duration: 10000,
					isClosable: true,
					position: "top-right",
				});
			}
			return null
        }
    }))

	const reDrawChildren = () => {
		if (layerRef?.current) {
			let { height, width } = prevBounds.get();
			Object.keys(elementsData).forEach((element) => {
				if (height > 0 && width > 0) {
					let factorX = konvaBounds.width.get() / width;
					let factorY = konvaBounds.height.get() / height;
					// @ts-ignore
					let attrs = elementsData[element].data;
					attrs.merge({ x: attrs.x.value * factorX, y: attrs.y.value * factorY });
					if ("fontSize" in attrs) attrs.fontSize.set(attrs.fontSize.value * factorX);

					if ("size" in attrs) attrs.merge({ size: attrs.size.value * factorX });

					// @ts-ignore
					if ("width" in attrs) attrs.merge({ width: attrs.width.value * factorX });
					// @ts-ignore
					if ("height" in attrs) attrs.merge({ height: attrs.height.value * factorY });
				}
			});
			// update the previous bounds of the board
			prevBounds.set({ ...konvaBounds.get() });
		}
	};

	const initRenderSizing = () => {
		if (loader.value) return;
		// @ts-ignore
		if (!configData.value.rendered_dimensions) return;
		// @ts-ignore
		scale.set(konvaBounds.value.height / configData.value?.rendered_dimensions.height);

		if (layerRef?.current) {
			Object.keys(elementsData).forEach((element) => {
				// @ts-ignore
				let attrs = elementsData[element].data;
				attrs.merge({ x: attrs.x.value * scale.value, y: attrs.y.value * scale.value });
				if ("fontSize" in attrs) attrs.fontSize.set(attrs.fontSize.value * scale.value);

				if ("size" in attrs) attrs.merge({ size: attrs.size.value * scale.value });

				// @ts-ignore
				if ("width" in attrs) attrs.merge({ width: attrs.width.value * scale.value });
				// @ts-ignore
				if ("height" in attrs) attrs.merge({ height: attrs.height.value * scale.value });
			});
			loader.set(true);
		}
	};

	const initKonvaSize = () => {
		if (isLoading.get()) return; // template image not loaded yet
		const bounds = document.getElementById("preview_certificate")?.getBoundingClientRect();
		if (!bounds || bounds.width === 0 || bounds.height === 0) return; // not laid out yet
		canvasBounds.set(bounds);

		// contain-fit the template image inside the container
		const boxWidth = bounds.width;
		const boxHeight = bounds.height;
		const imgRatio = bgImgBounds.width.get() / bgImgBounds.height.get();
		const fit = imgRatio > boxWidth / boxHeight
			? { width: boxWidth, height: boxWidth / imgRatio }
			: { height: boxHeight, width: boxHeight * imgRatio };
		konvaBounds.merge({
			...fit,
			top: Math.max(0, (boxHeight - fit.height) / 2),
			left: Math.max(0, (boxWidth - fit.width) / 2),
		});

		initRenderSizing();

		// redraw elements
		reDrawChildren();
	};

	const jugad = () => {
		Object.keys(elementsData).forEach((element) => {
			// @ts-ignore
			let attrs = elementsData[element].data;
			// @ts-ignore
			if (elementsData[element]['type'].value === 'text') {
				let realWidth = attrs.width.value
				attrs.width.set(realWidth - 1)
				attrs.width.set(realWidth)
			}
		});
		loader.set(true);
	}

	const handleImageLoaded = (width: number, height: number) => {
		setImage(imageRef.current);

		// bgImgBounds.set({ height: image?.height || 0, width: image?.width || 0 });
		bgImgBounds.set({ height: height || 0, width: width || 0 });
		// isLoading must flip first so the Stage mounts (layerRef) before sizing runs; relies on React 17 sync render
		isLoading.set(false);
		initKonvaSize();
		setTimeout(() => {
			//jugad
			jugad()
		}, 100);
	};

	const loadImage = () => {
		const img = new window.Image();
		img.src = bg;
		img.crossOrigin = "Anonymous";
		imageRef.current = img;
		imageRef.current.addEventListener("load", () => {
			handleImageLoaded(img.width, img.height);
		});
	};

	useEffect(() => {
		loadImage();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bg]);

	useEffect(() => {
		// Re-fit whenever the container changes size (breakpoint reflow, sidebar stacking), not just on window resize
		const container = document.getElementById("preview_certificate");
		const ResizeObserverCtor = (window as any).ResizeObserver; // no type in TS 3.9
		if (container && ResizeObserverCtor) {
			const observer = new ResizeObserverCtor(() => initKonvaSize());
			observer.observe(container);
			return () => observer.disconnect();
		}
		const onResize = () => initKonvaSize();
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return !isLoading.get() && image ? (
		<>
			<Stage
				height={konvaBounds.height.get()}
				width={konvaBounds.width.get()}
				style={{ position: "absolute", marginLeft: konvaBounds.left.get(), marginTop: konvaBounds.top.get() }}>
				{imageURL.get().length > 0 && (
					<Layer ref={layerRef}>
						{/* Background Image should be the first element in this layer */}
						<Image height={konvaBounds.height.get()} width={konvaBounds.width.get()} image={image} />
						<DrawingSheet elementsData={elementsData} imageElementsHolder={imageElementsHolder} isWhiteLabeled={isWhiteLabeled} orgConfig={orgConfig} />
					</Layer>
				)}
			</Stage>
		</>
	) : (
		<Skeleton position="absolute" top={0} left={0} w="100%" h="100%" />
	);
});

export default PreviewCertificate;
